// One shape for everything AHFC is owed, wherever it was filed.
//
// Receivables come from two places: claim_lines (home health — built into a
// run, or recorded by hand for a claim filed outside CAERA) and dme_orders
// (DME — billed to CareSource and tracked on the order itself). The
// Receivables tab, the Finance overview and the P&L used to each carry their
// own mapping, and they disagreed: one read `approved` as "Submitted", so in
// shadow mode every run looked filed when none was. This is the one mapping.
//
// Statuses are claim_lines.status as stored. `approved` means built and not
// yet filed — it is not a receivable and is never aged.

import { payerLabel, destinationLabel, DME_PAYER } from './serviceModel';

export { DME_PAYER };

export const RECEIVABLE_STATUS = {
  pending:   { label: 'Pending',   bg: 'var(--bg2)',        color: 'var(--text3)',   filed: false, open: false },
  approved:  { label: 'Not filed', bg: 'var(--info-bg)',    color: 'var(--info)',    filed: false, open: false },
  submitted: { label: 'Submitted', bg: 'var(--warn-bg)',    color: 'var(--warn)',    filed: true,  open: true  },
  adjusted:  { label: 'Adjusted',  bg: 'var(--warn-bg)',    color: 'var(--warn)',    filed: true,  open: true  },
  denied:    { label: 'Denied',    bg: 'var(--danger-bg)',  color: 'var(--danger)',  filed: true,  open: true  },
  paid:      { label: 'Paid',      bg: 'var(--success-bg)', color: 'var(--success)', filed: true,  open: false },
  voided:    { label: 'Voided',    bg: 'var(--bg2)',        color: 'var(--text3)',   filed: true,  open: false },
};

export const statusInfo = (status) => RECEIVABLE_STATUS[status] || RECEIVABLE_STATUS.pending;

/** Filed and still waiting on money or a decision. What AR aging counts. */
export const isOutstanding = (r) => statusInfo(r.status).open;

/** Built but never filed. Shown, never aged. */
export const isUnfiled = (r) => !statusInfo(r.status).filed;

/** Statuses a hand-recorded claim can be given. It was filed, by definition. */
export const MANUAL_STATUSES = ['submitted', 'paid', 'denied', 'adjusted', 'voided'];

/**
 * When a claim was filed, for aging. Lines filed through CAERA carry
 * submitted_at; lines posted from a remittance before that column existed
 * carry an ICN and nothing else, and older manual-ICN runs stamp the run.
 * The service date is the last resort, and is what every line before
 * 2026-09 falls back to.
 */
export const filedOn = (line, run) =>
  (line.submitted_at || '').slice(0, 10)
  || (run?.submitted_at || '').slice(0, 10)
  || line.service_date
  || null;

/** A home-health claim line, as a receivable. `run` is optional. */
export const lineToReceivable = (l, run) => ({
  _source: l.source === 'manual' ? 'manual' : 'hh',
  id: `cl_${l.id}`, line_id: l.id,
  client_id: l.client_id || null,
  client_name: l.client_name, medicaid_id: l.medicaid_id || '',
  payer: l.payer ? payerLabel(l.payer) : destinationLabel(run?.payer),
  payer_key: l.payer || null,
  service_code: l.service_code, service_date: l.service_date,
  hours: l.hours != null ? Number(l.hours) : null,
  amount: Number(l.accrued_amount ?? l.charge_amount ?? 0),
  paid_amount: l.paid_amount != null ? Number(l.paid_amount) : null,
  status: l.status,
  submitted_date: filedOn(l, run), paid_date: l.paid_date || null,
  icn: l.icn || null,
  denial_code: l.denial_code || (l.eobs_codes ? l.eobs_codes.split(' ')[0] : null),
  denial_reason: l.denial_reason || null,
  eobs_codes: l.eobs_codes || null,
  notes: l.notes || null, billing_run_id: l.billing_run_id || null,
});

/** A DME order that has been claimed, as a receivable. */
export const dmeToReceivable = (o) => ({
  _source: 'dme',
  id: `dme_${o.id}`, order_id: o.id,
  client_id: o.client_id || null,
  client_name: o.client_name, medicaid_id: o.client?.medicaid_id || '',
  payer: payerLabel(DME_PAYER), payer_key: DME_PAYER,
  service_code: o.hcpcs_code, service_date: o.pod_delivered_date || o.delivered_date || o.claim_submitted_date,
  hours: null,
  amount: Number(o.claim_amount ?? o.expected_amount ?? 0),
  paid_amount: o.paid_amount != null ? Number(o.paid_amount) : null,
  status: o.status === 'claim_paid' ? 'paid' : o.status === 'claim_denied' ? 'denied' : 'submitted',
  submitted_date: o.claim_submitted_date || null, paid_date: o.claim_paid_date || null,
  icn: o.claim_number || null,
  denial_code: null, denial_reason: o.claim_denial_reason || null, eobs_codes: null,
  notes: o.product_description || null, order_ref: o.order_ref,
});

/** DME order statuses that mean a claim exists. */
export const DME_CLAIMED_STATUSES = ['claim_submitted', 'claim_denied', 'claim_paid'];

/** Whole days between a filed date and `now`, or null. */
export const daysOutstanding = (r, now = new Date()) =>
  r.submitted_date ? Math.floor((now - new Date(`${r.submitted_date}T12:00:00`)) / 86400000) : null;

/** AR aging buckets over the outstanding receivables. */
export const ageReceivables = (rows, now = new Date()) => {
  const buckets = { a: 0, b: 0, c: 0, d: 0, aCt: 0, bCt: 0, cCt: 0, dCt: 0 };
  for (const r of rows) {
    if (!isOutstanding(r)) continue;
    const days = daysOutstanding(r, now);
    if (days == null) continue;
    const k = days <= 30 ? 'a' : days <= 60 ? 'b' : days <= 90 ? 'c' : 'd';
    buckets[k] += r.amount || 0; buckets[`${k}Ct`]++;
  }
  return buckets;
};
